import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button, Dialog, toastManager } from '@trove/ui';
import { Copy, X } from '@phosphor-icons/react';
import { loadCliGuideDismissed, saveCliGuideDismissed } from '../preferences';
import styles from './SidebarCliGuide.module.css';

const steps = [
  {
    label: 'Install',
    command: 'npm i -g @fldr/trove',
  },
  {
    label: 'Connect this terminal',
    command: 'trove auth login',
  },
  {
    label: 'Clone into any folder',
    command: 'trove clone ~/my-repo',
  },
] as const;

async function copyCommand(command: string) {
  try {
    await navigator.clipboard.writeText(command);
  } catch {
    toastManager.add({ title: 'Could not copy' });
  }
}

function CommandButton({ command }: { command: string }) {
  return (
    <button
      type="button"
      className={styles.command}
      title="Copy command"
      onClick={() => void copyCommand(command)}
    >
      <code className={styles.commandText}>{command}</code>
      <Copy className={styles.commandCopy} size={12} aria-hidden />
    </button>
  );
}

export function SidebarCliGuide({
  inert,
  embedded = false,
  onDismissedChange,
}: {
  inert?: boolean;
  embedded?: boolean;
  onDismissedChange?: (dismissed: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(() => loadCliGuideDismissed());
  const navigate = useNavigate();

  if (dismissed) return null;

  const callout = (
    <div className={styles.callout}>
      <button
        type="button"
        className={styles.calloutAction}
        tabIndex={inert ? -1 : undefined}
        onClick={() => setOpen(true)}
      >
        Want to work with Trove with your agents?
      </button>
      <button
        type="button"
        className={styles.dismiss}
        aria-label="Dismiss"
        tabIndex={inert ? -1 : undefined}
        onClick={() => {
          saveCliGuideDismissed(true);
          setDismissed(true);
          onDismissedChange?.(true);
        }}
      >
        <X size={12} aria-hidden />
      </button>
    </div>
  );

  return (
    <div className={embedded ? undefined : styles.wrap}>
      {callout}

      <Dialog
        open={open}
        onOpenChange={setOpen}
        title="Work with agents"
        description="Install the CLI, authorize this browser, then clone into any folder your agents can use."
        actions={
          <>
            <Button variant="ghost" onClick={() => setOpen(false)}>
              Close
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setOpen(false);
                void navigate({ to: '/connect' });
              }}
            >
              Open Connect
            </Button>
          </>
        }
      >
        <ol className={styles.steps}>
          {steps.map((step, index) => (
            <li key={step.command} className={styles.step}>
              <span className={styles.stepIndex}>{index + 1}</span>
              <div className={styles.stepBody}>
                <span className={styles.stepLabel}>{step.label}</span>
                <CommandButton command={step.command} />
              </div>
            </li>
          ))}
        </ol>
        <p className={styles.hint}>
          After cloning, pull with <code>git pull --rebase</code> before pushing. Only{' '}
          <code>main</code> is supported.
        </p>
      </Dialog>
    </div>
  );
}
